import React, {  useState, useMemo } from 'react';
import { TextField, MenuItem, useTheme, Typography, Checkbox, ListItemText,Box,InputAdornment } from '@mui/material';

const CustomSingleSelect = ({
  label,
  placeholder,
  name,
  register,
  options = [],
  required = false,
  error,
  searchable = false,
  value,
  onChange,
  borderColor,
  helperText,
  disabled = false,
  labelSx = {},
  inputSx = {},
  maxHeightDropdown= 210,
  ...props
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [searchTerm, setSearchTerm] = useState("");
  
const filteredOptions = useMemo(() => {
  if (!searchable || !searchTerm) return options;
  return options.filter((option) =>
    String(option.label).toLowerCase().includes(searchTerm.toLowerCase())
  );
}, [options, searchTerm, searchable]);


  const labelBackground = required
    ? isDark
      ? '#660000'
      : 'linear-gradient(135deg, #673ab7, #9575cd)'
    : isDark
      ? '#003366'
      : 'linear-gradient(135deg, #2196f3, #21cbf3)';

  const inputLabelProps = {
    shrink: true,
    sx: {
      background: disabled ? (isDark ? '#444444' : '#cccccc') : labelBackground,
      fontWeight: 700,
      clipPath: 'polygon(0% 0%, 100% 0%, 95% 100%, 5% 100%)',
      padding: '4px 14px',
      top: '-4px',
      borderRadius: '0',
      color: disabled ? (isDark ? '#aaa' : '#333') : '#fff',
      fontSize: '12px',
     '&.Mui-focused': { color: disabled ? '#aaa' : '#fff' },

      '&.Mui-error': {
      color: disabled
        ? (isDark ? '#aaa' : '#333')
        : '#fff',
      '.MuiFormLabel-asterisk': {
        color: disabled
          ? (isDark ? '#aaa' : '#333')
          : '#fff',
      },
    },
      ...labelSx,
    },
  };

  const textFieldSx = {
    width: '100%',
    borderColor: borderColor || (disabled
      ? '#b0b0b0'
      : theme.palette.custom?.InputborderColor || theme.palette.divider),
    '& .MuiOutlinedInput-root': {
      overflow: 'hidden',
      borderColor: borderColor || (disabled
        ? '#b0b0b0'
        : theme.palette.custom?.InputborderColor || theme.palette.divider),
      borderRadius: '8px',
      // height: '44px',
      backgroundColor: disabled ? '#e0e0e0' : theme.palette.background.paper,
      color: disabled
        ? '#666666'
        : theme.palette.background?.textblack || theme.palette.text.primary,
      '& .MuiSelect-select': {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.background?.textblack || theme.palette.text.primary,
        padding: '10px 14px',
        '&::placeholder': {
          color: isDark ? '#aaa' : '#333',
          fontSize: '12px',
          opacity: 1,
        },
        ...inputSx,
      },
      '& .MuiInputBase-input': {
        fontSize: '16px',
      },
      '&:hover fieldset': {
         pointerEvents: 'none',
      },
      '&.Mui-focused fieldset': {
        borderColor: borderColor || (disabled
          ? '#b0b0b0'
          : theme.palette.custom?.InputborderColor || theme.palette.divider),
      },
    },
    '& fieldset': {
      padding: 0,
      borderRadius: '8px',
      top: 0,
      borderColor: borderColor || (disabled
        ? '#b0b0b0'
        : theme.palette.custom?.InputborderColor || theme.palette.divider),
    },
    '& fieldset legend': {
      display: 'none',
    },
  };

  const registerProps = register ? register(name) : {};

  return (
    <TextField
      size="small"
      select
      label={label}
      placeholder={placeholder}
      variant="outlined"
      required={required}
      InputLabelProps={inputLabelProps}
      sx={textFieldSx}
      error={!!error}
      value={value || (props.multiple ? [] : '')}
      onChange={onChange}
      disabled={disabled}
      helperText={error ? helperText : ' '}
      FormHelperTextProps={{
        sx: {
          position: 'absolute',
          top: '-16px',
          right: '8px',
          color: 'red',
          fontSize: '10px',
          margin: 0,
        },
      }}
      SelectProps={{
        multiple: props.multiple,
        displayEmpty: true,
        renderValue: selected => {
          if (!selected || (Array.isArray(selected) && selected.length === 0)) {
            // return placeholder || 'Select an option';
            return (
              <Typography
                sx={{
                  fontSize: '14px',
                  color: isDark ? '#aaa' : '#333',
                  lineHeight: 'inherit',
                }}
              >
                {placeholder || 'Select'}
              </Typography>
            );
          }

          if (Array.isArray(selected)) {
            const selectedLabels = selected.includes('selectAll')
              ? options.filter(opt => opt.value !== 'selectAll').map(opt => opt.label)
              : options.filter(opt => selected.includes(opt.value)).map(opt => opt.label);
            if (selectedLabels.length > 2) {
              return `${selectedLabels[0]}, ${selectedLabels[1]}, +${selectedLabels.length - 2}`;
            }
            return selectedLabels.join(', ');
          }

          const selectedOption = options.find(opt => opt.value === selected);
          return selectedOption ? selectedOption.label : placeholder || '';
        },
        MenuProps: {
          PaperProps: {
            sx: {
              maxHeight: maxHeightDropdown,
            },
          },
        },
      }}
      {...registerProps}
      {...props}
    >
      {searchable && (
        <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <TextField
          fullWidth
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {/* <SearchIcon fontSize="small" /> */}
              </InputAdornment>
            ),
          }}
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              // backgroundColor: isDark ? "#333" : "#f5f5f5",
              display: "flex",
              alignItems: "center",
              border: `1px solid ${theme.palette.custom.borderColor}`,
              borderRadius: '8px',
              padding: "1px 8px",
              //backgroundColor: theme.palette.background.paper,
              // color: effectiveTextColor,
              "& .MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${theme.palette.custom.borderColor}`, // 🔁 Your custom border color
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${theme.palette.custom.borderColor}`,// Hover color
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${theme.palette.custom.borderColor}`, // Focused color
              },
            },
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </Box>
      )}

      {filteredOptions.length === 0 ? (
        <MenuItem value="" disabled>
            No options available
        </MenuItem>
      ) : (
        filteredOptions.map((option, index) => (
          <MenuItem key={index} value={option.value}>
            {props.multiple && (
              <Checkbox
                checked={
                  Array.isArray(value) &&
                  (value.includes(option.value) ||
                    (option.value === 'selectAll' &&
                      options
                        .filter(r => r.value !== 'selectAll')
                        .every(r => value.includes(r.value))) ||
                    (value.includes('selectAll') && option.value !== 'selectAll'))
                }
                sx={{
                  padding: "4px",
                  "& .MuiSvgIcon-root": {
                    fontSize: 18,
                  },
                  color: theme.palette.mode === 'light' ? '#d0d0d0' : '#EBF2F5',
                  '&.Mui-checked': {
                    color: '#8f01ff',
                  },
                  '&.MuiCheckbox-indeterminate': {
                    color: '#8f01ff',
                  },
                }}
              />
            )}
            <ListItemText primary={option.label} />
          </MenuItem>
        ))
      )}
    </TextField>
  );
};

export default CustomSingleSelect;
